<template>
  <div class="container mt-0">
    <!-- <h1>Irrigation Energy Cost Comparison</h1> -->

    <EsriDataTable
      @dictionaries-fetched="handleDictionariesFetched"
      @other-sources-fetched="handleOtherSourcesFetched"
    />

    <div class="row mb-3">
      <div class="col-md-6">
        <h2 class="mb-0">Table 1: Field Information</h2>

        <div class="goa-tableouter mt-2">
          <div class="goa-table">
            <table
              class="table table-bordered"
              @mouseover="tableMouseOver($event)"
              @mouseout="tableMouseOut()"
            >
              <thead>
                <tr>
                  <th class="parameter-column">Parameter</th>
                  <th class="value-column">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <!-- <HyperlinkCell :value="fieldName_link" label="Name" /> -->
                    <HyperlinkCell
                      :value="linkDict.fieldName_link"
                      label="Name"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="text"
                        class="borderless-input"
                        name="fieldName"
                        v-model="fieldName"
                        :class="{
                          'highlight-editable': isFieldEditable('fieldName'),
                        }"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.fieldLocation_link"
                      label="Field Location"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="text"
                        class="borderless-input"
                        name="fieldLocation"
                        v-model="fieldLocation"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('fieldLocation'),
                        }"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.cropSelected_link"
                      label="Crop Selected"
                    />
                  </td>
                  <td>
                    <select v-model="cropSelected" class="borderless-input">
                      <option
                        v-for="crop in cropOptions"
                        :value="crop"
                        :key="crop"
                      >
                        {{ crop }}
                      </option>
                    </select>
                  </td>
                </tr>

                <tr v-if="cropSelected === 'Other'">
                  <td></td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        class="borderless-input"
                        v-model="otherCropName"
                        name="otherCropName"
                        type="text"
                        placeholder="Enter crop name"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('otherCropName'),
                        }"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.irrigationSelected_link"
                      label="Irrigation Selected"
                    />
                  </td>
                  <td>
                    <select
                      v-model="irrigationSelected"
                      class="borderless-input"
                    >
                      <option
                        v-for="option in irrigationOptions"
                        :value="option"
                        :key="option"
                      >
                        {{ option }}
                      </option>
                    </select>
                  </td>
                </tr>

                <tr v-if="irrigationSelected === 'Other'">
                  <td></td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        class="borderless-input"
                        v-model="otherIrrigationName"
                        name="otherIrrigationName"
                        type="text"
                        placeholder="Enter irrigation method"
                        :class="{
                          'highlight-editable': isFieldEditable(
                            'otherIrrigationName'
                          ),
                        }"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="note-wrapper">
          <p class="note-label">Note:</p>
          <p class="note-content">
            This table provides information about the fields, crop type and
            irrigation methods. It is for information only.
          </p>
        </div>
      </div>

      <div class="col-md-6">
        <h2 class="mb-0">Table 2: Irrigation System</h2>

        <div class="goa-tableouter mt-2">
          <div class="goa-table">
            <table
              class="table table-bordered"
              @input="handleTableChange"
              @mouseover="tableMouseOver($event)"
              @mouseout="tableMouseOut()"
            >
              <thead>
                <tr>
                  <th class="parameter-column">Parameter</th>
                  <th class="value-column">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Irrigated Area (Acre)</td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="irrigatedArea"
                        v-model="irrigatedArea"
                        required
                        step="any"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('irrigatedArea'),
                          'value-changed': isFieldChanged('irrigatedArea'),
                        }"
                        @input="validateField('irrigatedArea', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.daysPerSeason_link"
                      label="Days per Season (Days)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="daysPerSeason"
                        v-model="daysPerSeason"
                        required
                        step="any"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('daysPerSeason'),
                          'value-changed': isFieldChanged('daysPerSeason'),
                        }"
                        @input="validateField('daysPerSeason', 0, 366)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.flowRate_link"
                      label="Flow Rate (US gpm)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="flowRate"
                        v-model="flowRate"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('flowRate'),
                          'value-changed': isFieldChanged('flowRate'),
                        }"
                        @input="validateField('flowRate', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.pumpPressure_link"
                      label="Pump Pressure (psi)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="pumpPressure"
                        v-model="pumpPressure"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('pumpPressure'),
                          'value-changed': isFieldChanged('pumpPressure'),
                        }"
                        @input="validateField('pumpPressure', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.pumpEfficiency_link"
                      label="Pump Efficiency (%)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="pumpEfficiency"
                        v-model="pumpEfficiency"
                        required
                        step="any"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('pumpEfficiency'),
                          'value-changed': isFieldChanged('pumpEfficiency'),
                        }"
                        @input="validateField('pumpEfficiency', 0, 100)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.hoursPerSeason_link"
                      label="Hours per Season (Hours)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="hoursPerSeason"
                        v-model="hoursPerSeason"
                        required
                        step="any"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('hoursPerSeason'),
                          'value-changed': isFieldChanged('hoursPerSeason'),
                        }"
                        @input="validateField('hoursPerSeason', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Gross Inches per Season(Inch)</td>
                  <td>
                    <input
                      type="number"
                      class="borderless-input no-spinners"
                      name="grossInchesSeason"
                      v-model="grossInchesSeason"
                      readonly
                    />
                  </td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->

                  <td>Braking Horsepower(Hp)</td>
                  <td>
                    <input
                      type="number"
                      class="borderless-input no-spinners"
                      name="brakingHP"
                      v-model="brakingHP"
                      @input="brakingHP = formatFieldDecimal(brakingHP)"
                      readonly
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-md-6">
        <h2 class="mb-0">Table 3: Electricity</h2>
        <div class="goa-tableouter mt-2">
          <div class="goa-table">
            <table
              class="table table-bordered"
              @input="handleTableChange"
              @mouseover="tableMouseOver($event)"
              @mouseout="tableMouseOut()"
            >
              <thead>
                <tr>
                  <th class="parameter-column">Parameter</th>
                  <th class="value-column">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.motorEfficiency_link"
                      label="Motor Efficiency (%)"
                    />
                  </td>

                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="motorEfficiency"
                        v-model="motorEfficiency"
                        required
                        step="any"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('motorEfficiency'),
                          'value-changed': isFieldChanged('motorEfficiency'),
                        }"
                        @input="validateField('motorEfficiency', 0, 100)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Motor capacity Required(kW)</td>
                  <td>{{ motorCapacity }}</td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Total KWH per season(kWh)</td>
                  <td>{{ totalKWH }}</td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.energyCharge_link"
                      label="Energy Charge ($/KWh)"
                    />

                    <!-- <a
                      href="https://electric.atco.com/content/dam/web/for-home/electricity/2023-01-01-atco-price-schedules-January23-update.pdf#page=9"
                      target="_blank"
                      >Energy Charge($/KWh)</a> -->
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="energyCharge"
                        v-model="energyCharge"
                        :class="{
                          'highlight-editable': isFieldEditable('energyCharge'),
                          'value-changed': isFieldChanged('energyCharge'),
                        }"
                        required
                        step="any"
                        @input="validateField('energyCharge', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.customerCharge_link"
                      label="Customer Charge ($/Day)"
                    />
                    <!-- <a href="https://electric.atco.com/content/dam/web/for-home/electricity/2023-01-01-atco-price-schedules-January23-update.pdf#page=9"
                      target="_blank">Customer Charge($/Day)</a> -->
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="customerCharge"
                        v-model="customerCharge"
                        required
                        step="any"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('customerCharge'),
                          'value-changed': isFieldChanged('customerCharge'),
                        }"
                        @input="validateField('customerCharge', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.demandCharge_link"
                      label="Demand Charge ($/kW-day)"
                    />
                    <!-- <a href="https://electric.atco.com/content/dam/web/for-home/electricity/2023-01-01-atco-price-schedules-January23-update.pdf#page=9"
                      target="_blank">
                      Demand Charge($/kW-day)</a> -->
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="demandCharge"
                        v-model="demandCharge"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('demandCharge'),
                          'value-changed': isFieldChanged('demandCharge'),
                        }"
                        @input="validateField('demandCharge', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.riderB_link"
                      label="Rider B ($/KWh)"
                    />
                    <!-- <a href="https://electric.atco.com/content/dam/web/atco-electric/rates/2023-01-01-atco-rider-b.pdf#page=0"
                      target="_blank">Rider B($/KWh)</a> -->
                  </td>

                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="riderB"
                        v-model="riderB"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('riderB'),
                          'value-changed': isFieldChanged('riderB'),
                        }"
                        @input="validateField('riderB', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.riderG_link"
                      label="Rider G ($/KWh)"
                    />
                    <!-- <a href="https://electric.atco.com/content/dam/web/atco-electric/rates/2023-01-01-atco-rider-g.pdf#page=1"
                      target="_blank">
                      Rider G($/KWh)</a> -->
                  </td>

                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="riderG"
                        v-model="riderG"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('riderG'),
                          'value-changed': isFieldChanged('riderG'),
                        }"
                        @input="validateField('riderG', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.riderJ_link"
                      label="Rider J ($/KWh)"
                    />
                    <!-- <a href="https://electric.atco.com/content/dam/web/atco-electric/rates/2023-01-01-atco-rider-j.pdf#page=1"
                      target="_blank">Rider J(%)</a> -->
                  </td>

                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="riderJ"
                        v-model="riderJ"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('riderJ'),
                          'value-changed': isFieldChanged('riderJ'),
                        }"
                        @input="validateField('riderJ', 0, 100)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.adminFee_link"
                      label="Admin Fee ($/Month)"
                    />
                  </td>

                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="adminFee"
                        v-model="adminFee"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('adminFee'),
                          'value-changed': isFieldChanged('adminFee'),
                        }"
                        @input="validateField('adminFee', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.rebateProg_link"
                      label="Electricity Rebate ($/kWh)"
                    />
                  </td>

                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="rebateProg"
                        v-model="rebateProg"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('rebateProg'),
                          'value-changed': isFieldChanged('rebateProg'),
                        }"
                        @input="validateField('rebateProg', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <h2 class="mb-0">Table 4: Natural Gas</h2>
        <div class="goa-tableouter mt-2">
          <div class="goa-table">
            <table
              class="table table-bordered"
              @input="handleTableChange"
              @mouseover="tableMouseOver($event)"
              @mouseout="tableMouseOut()"
            >
              <thead>
                <tr>
                  <th class="parameter-column">Parameter</th>
                  <th class="value-column">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.ngEngineEfficiency_link"
                      label="Engine Efficiency (%)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="ngEngineEfficiency"
                        v-model="ngEngineEfficiency"
                        required
                        step="any"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('ngEngineEfficiency'),
                          'value-changed': isFieldChanged('ngEngineEfficiency'),
                        }"
                        @input="validateField('ngEngineEfficiency', 0, 100)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Engine HP Required(HP)</td>
                  <td>{{ ngHPRequired }}</td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Total GL per season(GJ)</td>
                  <td>{{ ngTotalGJ }}</td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.ngGasPrice_link"
                      label="Natural Gas Price ($/GJ)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="ngGasPrice"
                        v-model="ngGasPrice"
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('ngGasPrice'),
                          'value-changed': isFieldChanged('ngGasPrice'),
                        }"
                        @input="validateField('ngGasPrice', 0)"
                        required
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.ngFixedDelivery_link"
                      label="Fixed Charge ($/Day)"
                    />
                    <!-- <a href="https://gas.atco.com/content/dam/web/for-home/natural-gas/natural-gas-south-rate-schedule.pdf#page=16"
                      target="_blank">Fixed Charge($/Day)</a> -->
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="ngFixedDelivery"
                        v-model="ngFixedDelivery"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('ngFixedDelivery'),
                          'value-changed': isFieldChanged('ngFixedDelivery'),
                        }"
                        @input="validateField('ngFixedDelivery', 0)"
                        required
                        step="any"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.ngVariableDelivery_link"
                      label="Variable Charge ($/Day)"
                    />
                    <!-- <a href="https://gas.atco.com/content/dam/web/for-home/natural-gas/natural-gas-south-rate-schedule.pdf#page=16"
                      target="_blank">
                      Variable Charge($/Day)</a> -->
                  </td>

                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="ngVariableDelivery"
                        v-model="ngVariableDelivery"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('ngVariableDelivery'),
                          'value-changed': isFieldChanged('ngVariableDelivery'),
                        }"
                        @input="validateField('ngVariableDelivery', 0)"
                        required
                        step="any"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.ngRiderL_link"
                      label="NG Rider L ($/GJ)"
                    />
                    <!-- <a href="https://gas.atco.com/content/dam/web/for-home/natural-gas/natural-gas-south-rate-schedule.pdf#page=8"
                      target="_blank">NG Rider L($/GJ)</a> -->
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="ngRiderL"
                        v-model="ngRiderL"
                        :class="{
                          'highlight-editable': isFieldEditable('ngRiderL'),
                          'value-changed': isFieldChanged('ngRiderL'),
                        }"
                        @input="validateField('ngRiderL', 0)"
                        required
                        step="any"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.ngAdminFee_link"
                      label="Admin Fee ($/Month)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="ngAdminFee"
                        v-model="ngAdminFee"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('ngAdminFee'),
                          'value-changed': isFieldChanged('ngAdminFee'),
                        }"
                        @input="validateField('ngAdminFee', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.ngRebateProg_link"
                      label="NG Rebate ($/GJ)"
                    />
                    <!-- <a href="https://ucahelps.alberta.ca/alberta-natural-gas-rebates.aspx#:~:text=If%20the%20published%20Alberta%20price,%241%20for%20every%20gigajoule%20used."
                      target="_blank">NG Rebate($/GJ)</a> -->
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="ngRebateProg"
                        v-model="ngRebateProg"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('ngRebateProg'),
                          'value-changed': isFieldChanged('ngRebateProg'),
                        }"
                        @input="validateField('ngRebateProg', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-md-6">
        <h2 class="mb-0">Table 5: Diesel</h2>
        <div class="goa-tableouter mt-2">
          <div class="goa-table">
            <table
              class="table table-bordered"
              @input="handleTableChange"
              @mouseover="tableMouseOver($event)"
              @mouseout="tableMouseOut()"
            >
              <thead>
                <tr>
                  <th class="parameter-column">Parameter</th>
                  <th class="value-column">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.dieselFuelRating_lbPerHPH_link"
                      label="Diesel Consumption Rating (lb/hp-hr)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="dieselFuelRating_lbPerHPH"
                        v-model="dieselFuelRating_lbPerHPH"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable(
                            'dieselFuelRating_lbPerHPH'
                          ),
                          'value-changed': isFieldChanged(
                            'dieselFuelRating_lbPerHPH'
                          ),
                        }"
                        @input="validateField('dieselFuelRating_lbPerHPH', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Engine HP Required(BHP)</td>

                  <td>
                    <input
                      type="number"
                      class="borderless-input no-spinners"
                      name="dieselEngineRequired"
                      v-model="dieselEngineRequired"
                      readonly
                    />
                  </td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Diesel Consumption Rating (litre/HP-Hour)</td>
                  <td>
                    {{ dieselFuelRating_literPerHPH }}
                  </td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Total Diesel consumption per season(Litre)</td>
                  <td>
                    {{ dieselTotalLitre }}
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.dieselPrice_link"
                      label="Diesel Price ($/Litre)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="dieselPrice"
                        v-model="dieselPrice"
                        :class="{
                          'highlight-editable': isFieldEditable('dieselPrice'),
                          'value-changed': isFieldChanged('dieselPrice'),
                        }"
                        @input="validateField('dieselPrice', 0)"
                        required
                        step="any"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.dieselRebateProg_link"
                      label="Diesel Rebate ($/Litre)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="dieselRebateProg"
                        v-model="dieselRebateProg"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('dieselRebateProg'),
                          'value-changed': isFieldChanged('dieselRebateProg'),
                        }"
                        @input="validateField('dieselRebateProg', 0)"
                        required
                        step="any"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <h2 class="mb-0">Table 6: Propane</h2>
        <div class="goa-tableouter mt-2">
          <div class="goa-table">
            <table
              class="table table-bordered"
              @input="handleTableChange"
              @mouseover="tableMouseOver($event)"
              @mouseout="tableMouseOut()"
            >
              <thead>
                <tr>
                  <!-- <th>Parameter</th>
              <th>Value</th> -->
                  <th class="parameter-column">Parameter</th>
                  <th class="value-column">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.propaneEngineEfficiency_link"
                      label="Engine Efficiency (%)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        id="propaneEngineEfficiency"
                        class="borderless-input no-spinners"
                        name="propaneEngineEfficiency"
                        v-model="propaneEngineEfficiency"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable(
                            'propaneEngineEfficiency'
                          ),
                          'value-changed': isFieldChanged(
                            'propaneEngineEfficiency'
                          ),
                        }"
                        @input="
                          validateField('propaneEngineEfficiency', 0, 100)
                        "
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Engine HP Required(BHP)</td>
                  <td>{{ propaneHPRequired }}</td>
                </tr>

                <tr style="display: none">
                  <!-- <tr> -->
                  <td>Total consumption pre season(Litre)</td>
                  <td>{{ propaneTotalConsumption }}</td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.propanePrice_link"
                      label="Propane Price ($/Litre)"
                    />
                    <!-- <a href="https://www.globalpetrolprices.com/Canada/lpg_prices/" target="_blank">
                      Propane Price($/Litre)</a> -->
                  </td>
                  <!-- <td id="propane_price" @mouseover="showLink('propane_price')" @mouseout="hideLink('propane_price')" :class="{ 'hovered': showLinks.propane_price }">
                <a v-if="showLinks['propane_price']" :href="links['propane_price']" target="_blank">Reference page for the propane price</a>
                <span v-else>Propane Price($/Litre)</span>
              </td> -->
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="propanePrice"
                        v-model="propanePrice"
                        required
                        step="any"
                        :class="{
                          'highlight-editable': isFieldEditable('propanePrice'),
                          'value-changed': isFieldChanged('propanePrice'),
                        }"
                        @input="validateField('propanePrice', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <HyperlinkCell
                      :value="linkDict.propaneRebate_link"
                      label="Propane Rebate ($/Litre)"
                    />
                  </td>
                  <td>
                    <div class="icon-input-wrapper">
                      <input
                        type="number"
                        class="borderless-input no-spinners"
                        name="propaneRebate"
                        v-model="propaneRebate"
                        required
                        step="any"
                        :class="{
                          'highlight-editable':
                            isFieldEditable('propaneRebate'),
                          'value-changed': isFieldChanged('propaneRebate'),
                        }"
                        @input="validateField('propaneRebate', 0)"
                      />
                      <i class="fa-solid fa-pencil"></i>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="row mb-0">
      <div class="col-md-12">
        <h2 class="mb-0">Comparison Table</h2>
        <div class="goa-tableouter mt-2">
          <div class="goa-table">
            <!-- table-secondary is a class from Bootstrape -->
            <table
              :class="[
                'table',
                'table-bordered',
                { 'table-secondary': isTableEdited },
              ]"
            >
              <thead>
                <tr>
                  <th style="width: 20%">Cost or Usage</th>
                  <th style="width: 20%">Electricity</th>
                  <th style="width: 20%">Natural Gas</th>
                  <th style="width: 20%">Diesel</th>
                  <th style="width: 20%">Propane</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>$/Season</td>
                  <td>{{ formatNumberDisplay(electricityCost) }}</td>
                  <td>{{ formatNumberDisplay(ngCost) }}</td>
                  <td>{{ formatNumberDisplay(dieselCost) }}</td>
                  <td>{{ formatNumberDisplay(propaneCost) }}</td>
                </tr>

                <tr>
                  <td>$/Acre</td>
                  <td>{{ formatNumberDisplay(electricityCostPerAcre) }}</td>
                  <td>{{ formatNumberDisplay(ngCostPerAcre) }}</td>
                  <td>{{ formatNumberDisplay(dieselCostPerAcre) }}</td>
                  <td>{{ formatNumberDisplay(propaneCostPerAcre) }}</td>
                </tr>

                <tr>
                  <td>$/Inch</td>
                  <td>{{ formatNumberDisplay(electricityCostPerInch) }}</td>
                  <td>{{ formatNumberDisplay(ngCostPerInch) }}</td>
                  <td>{{ formatNumberDisplay(dieselCostPerInch) }}</td>
                  <td>{{ formatNumberDisplay(propaneCostPerInch) }}</td>
                </tr>

                <tr>
                  <td>$/Acre-inch</td>
                  <td>{{ formatNumberDisplay(electricityCostPerAcreInch) }}</td>
                  <td>{{ formatNumberDisplay(ngCostPerAcreInch) }}</td>
                  <td>{{ formatNumberDisplay(dieselCostPerAcreInch) }}</td>
                  <td>{{ formatNumberDisplay(propaneCostPerAcreInch) }}</td>
                </tr>

                <tr>
                  <td>$/hp-hr</td>
                  <td>{{ formatNumberDisplay(electricityCostPerHpHr) }}</td>
                  <td>{{ formatNumberDisplay(ngCostPerHpHr) }}</td>
                  <td>{{ formatNumberDisplay(dieselCostPerHpHr) }}</td>
                  <td>{{ formatNumberDisplay(propaneCostPerHpHr) }}</td>
                </tr>

                <tr>
                  <td>Usage (kWh, GJ, or Litre)</td>
                  <td>{{ formatNumberDisplay(electricityUsage) }}</td>
                  <td>{{ formatNumberDisplay(ngUsage) }}</td>
                  <td>{{ formatNumberDisplay(dieselUsage) }}</td>
                  <td>{{ formatNumberDisplay(propaneUsage) }}</td>
                </tr>

                <tr>
                  <td>$/(kWh, GJ, or Litre)</td>
                  <td>{{ formatNumberDisplay(electricityCostPerUnit) }}</td>
                  <td>{{ formatNumberDisplay(ngCostPerUnit) }}</td>
                  <td>{{ formatNumberDisplay(dieselCostPerUnit) }}</td>
                  <td>{{ formatNumberDisplay(propaneCostPerUnit) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- <div class="row center-aligned mb-0 mt-0"> -->
    <div class="row text-center mb-0 mt-0">
      <div class="col-md-4">
        <button class="goa-button" @click="handleButtonClick">
          <span :class="{ 'bold-text': isTableEdited }">{{
            buttonLabelText
          }}</span>
        </button>
      </div>
      <div class="col-md-4 mb-3">
        <!-- <button class="custom-button" @click="resetFields">Reset</button> -->
        <button class="goa-button" @click="resetFields">Reset</button>
      </div>
      <div class="col-md-4 mb-3">
        <button class="goa-button" @click="printPDFSummary">
          Summary in PDF
        </button>
      </div>
    </div>

    <!-- ---- -->
    <div class="row mb-0 mt-3 goa-band--grey">
      <div class="goa-grid-100-100-100">
        <h2>Other irrigation related resources</h2>
        <ul>
          <li v-for="(item, index) in otherSourcesLink" :key="index">
            <a :href="item.linkUrl" target="_blank">{{ item.label }}</a>
          </li>
        </ul>
      </div>
    </div>

    <div class="row mb-0 mt-3 goa-band--white">
      <div class="goa-grid-100-100-100">
        <h2>Contact 310-FARM</h2>

        <p>
          Hours: 8:15 am to 4:30 pm (open Monday to Friday, closed statutory
          holidays)<br />
          Toll free: <a href="tel:3103276">310-FARM</a> (3276) (in Alberta)<br />
          Phone: <a href="tel:+14037427901">403-742-7901</a> (outside
          Alberta)<br />
          Email: <a href="mailto:310farm@gov.ab.ca">310farm@gov.ab.ca</a> or
          <a href="mailto:	leonard.hingley@gov.ab.ca">
            leonard.hingley@gov.ab.ca</a
          >
        </p>
      </div>
    </div>

    <hr />
    <div class="latest-update-note">
      <p>Last updated: {{ lastUpdatedDate }}</p>
    </div>

    <!-- ----- -->
  </div>
</template>

<script>
import { initializeData } from "./components/dataUtils";
import { cropOptions } from "./components/cropOptions";
import { irrigationOptions } from "./components/irrigationOptions";
import { formatDecimal } from "./components/numberUtils.js";
import { clampFieldValue } from "./components/validationUtils.js";
import { calculateCosts } from "./components/costCalculator.js";
import { downloadAsPDF } from "./components/pdfExport";
//import hyperlinkData from "./components/hyperlink_dict.js";
import EsriDataTable from "@/EsriTable.vue";

import mitt from "mitt";

 //console.log(hyperlinkData.energyCharge);


export default {
  components: {
    EsriDataTable,
    // ... other components
  },

  data() {
    return {
      // Table 1: Field Information
      fieldName: "",
      fieldLocation: "",
      cropSelected: "",
      otherCropName: "", // This will store the custom crop name input by the user.
      irrigationSelected: "",
      otherIrrigationName: "",

      // Table 2: Irrigation Information
      irrigatedArea: "",
      daysPerSeason: "",
      flowRate: "",
      pumpPressure: "",
      pumpEfficiency: "",
      hoursPerSeason: "",
      //grossInchesSeason: '',
      //brakingHP: '',

      // Table 3: Electricity
      motorEfficiency: "",
      // motorCapacity: '',
      // totalKWH: '',
      energyCharge: "",
      customerCharge: "",
      demandCharge: "",
      riderB: "",
      riderG: "",
      riderJ: "",
      adminFee: "",
      rebateProg: "",

      // Table 4: Natural Gas
      ngEngineEfficiency: "",
      //ngHPRequired: '',
      //ngTotalGJ: '',
      ngGasPrice: "",
      ngFixedDelivery: "",
      ngVariableDelivery: "",
      ngRiderL: "",
      ngAdminFee: "",
      ngRebateProg: "",

      // Table 5: Diesel
      dieselFuelRating_lbPerHPH: "",
      // dieselFuelRating_literPerHPH: '',
      //dieselEngineRequired: '',
      //dieselTotalLitre: '',
      dieselPrice: "",
      dieselRebateProg: "",

      // Table 6: Propane
      propaneEngineEfficiency: "",
      //propaneHPRequired: '',
      //propaneTotalConsumption: '',
      propanePrice: "",
      propaneRebate: "",

      // Comparison Table
      electricityCost: 0,
      electricityCostPerAcre: 0,
      electricityCostPerInch: 0,
      electricityCostPerAcreInch: 0,
      electricityCostPerHpHr: 0,
      electricityUsage: 0,
      electricityCostPerUnit: 0,

      ngCost: 0,
      ngCostPerAcre: 0,
      ngCostPerInch: 0,
      ngCostPerAcreInch: 0,
      ngCostPerHpHr: 0,
      ngUsage: 0,
      ngCostPerUnit: 0,

      dieselCost: 0,
      dieselCostPerAcre: 0,
      dieselCostPerInch: 0,
      dieselCostPerAcreInch: 0,
      dieselCostPerHpHr: 0,
      dieselUsage: 0,
      dieselCostPerUnit: 0,

      propaneCost: 0,
      propaneCostPerAcre: 0,
      propaneCostPerInch: 0,
      propaneCostPerAcreInch: 0,
      propaneCostPerHpHr: 0,
      propaneUsage: 0,
      propaneCostPerUnit: 0,

      //others
      cropOptions: cropOptions,
      irrigationOptions: irrigationOptions,

      lastUpdatedDate: "",

      isTableEdited: false, //if true, calculate-->recalculate
      isButtonPressed: false,

      editableField: null,

      defaultValues: {}, // Add defaultValues object
      // previousValues: {},
      // currentValues: {}, 

      
      //...hyperlinkData,
      internalDict: {},
      linkDict: {},
      otherSourcesLink: [],
      emitter: mitt(),
    };
  },
  mounted() {
    //  initializeData(this);
    this.emitter.on("childDataFetched", () => {
      initializeData(this);
    });
  },

  beforeUnmount() {
    this.emitter.off("childDataFetched");
  },

  // watch: {
  //   propanePrice(newVal, oldVal) {
  //       console.log('Old Value:', oldVal);
  //       console.log('New Value:', newVal);
  //   },
  // },

  computed: {
    buttonLabelText() {
      if (this.isButtonPressed) {
        return "Calculate";
      } else {
        return this.isTableEdited ? "Re-calculate" : "Calculate";
      }
    },
    grossInchesSeason() {
      return formatDecimal(
        (this.flowRate * this.hoursPerSeason) / (453 * this.irrigatedArea)
      );
    },
    brakingHP() {
      return formatDecimal(
        (this.flowRate * this.pumpPressure) / 1717 / (this.pumpEfficiency / 100)
      );
    },
    motorCapacity() {
      return formatDecimal(
        (this.brakingHP / (this.motorEfficiency / 100)) * 0.74569987
      );
    },
    totalKWH() {
      return formatDecimal(this.motorCapacity * this.hoursPerSeason);
    },
    ngHPRequired() {
      return this.brakingHP;
    },
    ngTotalGJ() {
      return formatDecimal(
        (this.ngHPRequired * this.hoursPerSeason * 0.0026845) /
          (this.ngEngineEfficiency / 100)
      );
    },

    dieselEngineRequired() {
      return this.brakingHP;
    },

    dieselFuelRating_literPerHPH() {
      return formatDecimal((this.dieselFuelRating_lbPerHPH * 450) / 860);
    },
    dieselTotalLitre() {
      return formatDecimal(
        this.dieselFuelRating_literPerHPH * this.brakingHP * this.hoursPerSeason
      );
    },
    propaneHPRequired() {
      return this.brakingHP;
    },
    propaneTotalConsumption() {
      return formatDecimal(
        ((0.0026845195376962 * this.propaneHPRequired * this.hoursPerSeason) /
          (this.propaneEngineEfficiency / 100)) *
          39.072
      );
    },
  },

  methods: {
    handleDictionariesFetched(dictionaries) {
      this.internalDict = dictionaries.internalDict;
      this.linkDict = dictionaries.linkDict;
      // Log the content of both dictionaries
      //console.log("Internal Dictionary from App.vue:", this.internalDict);
      //console.log("Link Dictionary from App.vue:", this.linkDict);
      // Emit a custom event to signify completion
      this.emitter.emit("childDataFetched");
    },

    handleOtherSourcesFetched(data) {
      // handle otherSourcesData here
      this.otherSourcesLink = data;
      //console.log('Received other sources data:', this.otherSourcesLink);
    },

    isFieldChanged(fieldName) {
      return this[fieldName] !== this.defaultValues[fieldName];
    },

    // Call the imported formatDecimal function
    formatFieldDecimal(value) {
      return formatDecimal(value);
    },

    formatNumberDisplay(value) {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },

    validateField(fieldName, minValue, maxValue, enforceNonNegativity = false) {
      clampFieldValue(
        this,
        fieldName,
        minValue,
        maxValue,
        enforceNonNegativity
      );
    },

    handleTableChange() {
      this.isTableEdited = true;
      this.isButtonPressed = false;
    },

    validateNegtiveData() {
      let problematicSources = [];

      if (this.electricityCost <= 0) {
        problematicSources.push("Electricity");
      }

      if (this.ngCost <= 0) {
        problematicSources.push("Natural Gas");
      }

      if (this.dieselCost <= 0) {
        problematicSources.push("Diesel");
      }

      if (this.propaneCost <= 0) {
        problematicSources.push("Propane");
      }
      return problematicSources;
    },

    // Method to calculate the cost for each energy source and populate the comparison table
    handleButtonClick() {
      calculateCosts(this);

      this.isButtonPressed = true;
      this.isTableEdited = false;
      //console.log("button pressed");
      // console.log(this.defaultValues.lastUpdatedDate)
      
      //warning
      let problematicSources = this.validateNegtiveData();

      if (problematicSources.length > 0) {
        let tableSources = problematicSources.join(", ");
        let message = `Negative costs for <b>${tableSources}</b> found, and have been reset to zero in <b>Comparison Table</b>.<br> Please double check the input tables.`;

        this.$alert(message, "Warning");
      }
    },

    resetFields() {
      // Reset all the input fields to default values
      //console.log("Resetting the calculator...");
      initializeData(this);
      this.isTableEdited = false;
    },

    printPDFSummary() {
      // Generate and print a PDF summary based on the calculated costs
      //console.log("printSummaryPDF");
      // console.log(this.isTableEdited);
      // console.log(this.electricityUsage);
      if (this.isTableEdited) {
        // alert(
        //   "The table has been changed, please first click the calculate button"
        // );
        this.$alert(
          "Some input values may have been changed, please click the <b>Re_calculate</b> button to refresh.",
          "Warning"
        );
      } else if (this.isTableSumZero()) {
        this.$alert(
          "The cost has not been calculated, Please click the <b>Calculate</b> button first",
          "Warning"
        );
      } else {
        downloadAsPDF("Irrigation Energy Calculater", "Irrigation_cost.pdf");
      }

      // downloadAsPDF("Irrigation Energy Calculater", "Irrigation_cost.pdf");
    },

    isTableSumZero() {
      let sum =
        this.electricityCost + this.ngCost + this.dieselCost + this.propaneCost;
      return sum === 0;
    },

    //added on 2023-09-06
    setEditableField(fieldName) {
      this.editableField = fieldName;
    },

    clearEditableField() {
      this.editableField = null;
    },

    isFieldEditable(fieldName) {
      return this.editableField === fieldName;
    },

    tableMouseOver(event) {
      // If the target is an INPUT
      if (event.target.tagName === "INPUT") {
        this.setEditableField(event.target.name);
      }
      // Or if the target has a parent and that parent is an INPUT
      else if (
        event.target.parentElement &&
        event.target.parentElement.tagName === "INPUT"
      ) {
        this.setEditableField(event.target.parentElement.name);
      }
    },

    tableMouseOut() {
      this.clearEditableField();
    },
  },
};
</script>

<style scoped>
.borderless-input {
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  box-shadow: none;
  padding: 0;
  line-height: 1;
  box-sizing: border-box;
  /* Ensures any padding or border doesn't add to the input's total height */
}

input:focus {
  outline: 2px solid #fdc688;
  outline-offset: 0;
}

.no-spinners::-webkit-inner-spin-button,
.no-spinners::-webkit-outer-spin-button {
  display: none;
  -webkit-appearance: none;
  margin: 0;
}

.no-spinners {
  -webkit-appearance: textfield;
  /* Chrome, Safari, newer versions of Opera */
  -moz-appearance: textfield;
  /* Firefox */
  appearance: textfield;
  /* Standard, might not be needed yet */
}

/* control the color of the button --Re-calculate */
.bold-text {
  font-weight: bold;
  color: #fb8f25;
}

.note-wrapper {
  display: flex;
  align-items: flex-start;
}

.note-label {
  font-weight: bold;
  font-style: normal;
  /* font-size: 0.9em; */
  margin-left: 1em;
  width: 3em;
  color: #0081ab;
  /* Adjust this value to match the length of "Note: " */
}

.note-content {
  margin-left: 0.5em;
  /* font-size: 13px; */
  color: #0081ab;
  font-style: italic;
  /* Adjust this value as needed */
}

.value-changed {
  color: #36424a;
  font-weight: bold;
}

.latest-update-note {
  bottom: 0;
  left: 0;
  background-color: white;
  padding: 5px;
  font-size: 0.2rem;
  font-style: italic;
}

.highlight-editable {
  background-color: #f1f1f1;
}

.parameter-column {
  width: 60%;
}

.value-column {
  width: 40%;
}

.icon-input-wrapper {
  position: relative;
  display: inline-block;
}

.icon-input-wrapper > i {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.icon-input-wrapper > input {
  padding-right: 30px;
  /* Adjust based on icon size and spacing */
}

.icon-input-wrapper > i.fa-pencil {
  color: #0081ab;
  font-size: 0.7em;
}
</style>
